import { computed, type MaybeRef, unref, type Ref, toValue } from "vue";

import type { components as OpenAPI, operations } from "@/autogen/openapi";
import { ok } from "@/lib/openapi";

import api from "../../base";
import { useGenericAggregateQuery, useGenericListingQuery } from "../generic";
import { useNewsletterId } from "../utils";

import { createOrderingFromValues } from "@/lib/composables/ordering";

type EmailOrderingValues = NonNullable<
  operations["list_emails"]["parameters"]["query"]["ordering"]
>;
const EMAIL_ORDERING_VALUES: EmailOrderingValues[] = [
  "creation_date",
  "-creation_date",
  "status",
  "-status",
  "publish_date",
  "-publish_date",
  "subject",
  "-subject",
  "email_type",
  "-email_type",
  "modification_date",
  "-modification_date",
  "clicks",
  "-clicks",
  "opens",
  "-opens",
  "click_rate",
  "-click_rate",
  "open_rate",
  "-open_rate",
  "page_views_lifetime",
  "-page_views_lifetime",
  "page_views_30",
  "-page_views_30",
  "page_views_7",
  "-page_views_7",
  "webmentions",
  "-webmentions",
  "subscriptions",
  "-subscriptions",
  "paid_subscriptions",
  "-paid_subscriptions",
  "unsubscriptions",
  "-unsubscriptions",
  "replies",
  "-replies",
  "comments",
  "-comments",
  "social_mentions",
  "-social_mentions",
  "permanent_failure_rate",
  "-permanent_failure_rate",
  "complaint_rate",
  "-complaint_rate",
];
export const emailOrdering = createOrderingFromValues(EMAIL_ORDERING_VALUES);

type EmailStatus = OpenAPI["schemas"]["EmailStatus"];
type EmailType = OpenAPI["schemas"]["EmailType"];

export type EmailPage = OpenAPI["schemas"]["EmailPage"];

export type EmailFilterParameters = Omit<
  operations["list_emails"]["parameters"]["query"],
  "ids" | "excluded_fields" | "ordering" | "subject" | "negative_status"
>;

type EmailOrdering = NonNullable<
  operations["list_emails"]["parameters"]["query"]["ordering"]
>;

export const useEmailsListingQuery = ({
  parameters,
  ordering = "-creation_date",
  maintainData = false,
}: {
  parameters?: MaybeRef<EmailFilterParameters | undefined>;
  ordering?: MaybeRef<EmailOrdering>;
  maintainData?: boolean;
} = {}) => {
  const newsletterId = useNewsletterId();

  const query = useGenericListingQuery({
    queryKey: [
      newsletterId,
      "emails",
      "list",
      {
        ordering: ordering,
        parameters: computed(() => {
          return unref(parameters) ?? {};
        }),
      },
    ],
    maintainData: maintainData,
    async fetch({ page, signal }): Promise<EmailPage> {
      const data = await ok(
        api.get("/emails", {
          signal,
          params: {
            query: {
              ...unref(parameters),
              ordering: unref(ordering),
              page: page,
            },
          },
        })
      );

      return data;
    },
  });

  return query;
};

export const useEmailsAggregatesQuery = ({
  parameters,
}: {
  parameters?: MaybeRef<EmailFilterParameters>;
} = {}) => {
  const newsletterId = useNewsletterId();

  const query = useGenericAggregateQuery<EmailFilterParameters>({
    queryKey: [
      newsletterId,
      "emails",
      "aggregates",
      {
        parameters: computed(() => {
          return unref(parameters) ?? {};
        }),
      },
    ],
    resource: "email",
    parameters,
  });

  return query;
};

// normally you would've used `useSearchQuery`, but related emails requires
// filtering for a specific allowed status
export const useEmailSearchQuery = ({
  q: qRef,
  ordering = "-creation_date",
  status: statusRef,
  types: typeRef,
  maintainData = false,
  enabled: isEnabled,
}: {
  q: MaybeRef<string>;
  ordering?: MaybeRef<EmailOrdering>;
  status?: MaybeRef<EmailStatus[] | undefined>;
  types?: MaybeRef<EmailType[] | undefined>;
  maintainData?: boolean;
  enabled?: Ref<boolean>;
}) => {
  const newsletterId = useNewsletterId();

  const normalizedQuery = computed(() => {
    return (
      unref(qRef)
        // trim leading, trailing and repetitive whitespace
        .replace(/^ +|[ .,@]+$| +(?= )/g, "")
        // lowercase everything
        .toLowerCase()
    );
  });

  const normalizedStatus = computed(() => {
    return (toValue(statusRef) ?? []).toSorted();
  });

  const normalizedType = computed(() => {
    return (toValue(typeRef) ?? []).toSorted();
  });

  const query = useGenericListingQuery({
    queryKey: [
      newsletterId,
      "emails",
      "list",
      {
        ordering: ordering,
        query: normalizedQuery,
        status: normalizedStatus,
        type: normalizedType,
      },
    ],
    enabled: isEnabled,
    maintainData: maintainData,
    async fetch({ page, signal }) {
      const query = normalizedQuery.value;
      const status = normalizedStatus.value;
      const type = normalizedType.value;

      const data = await ok(
        api.get("/emails", {
          signal,
          params: {
            query: {
              page: page,
              ordering: unref(ordering),
              subject: query.length !== 0 ? query : undefined,
              status: status,
              email_type: type,
            },
          },
        })
      );

      return data;
    },
  });

  return query;
};

const PREMIUM_TYPES: EmailType[] = ["premium"];
const PUBLIC_TYPES: EmailType[] = ["public", "free", "churned"];

export const useLatestPublishedEmail = ({
  visibility,
  enabled,
}: {
  visibility?: MaybeRef<"all" | "public" | "premium" | undefined>;
  enabled?: Ref<boolean>;
} = {}) => {
  const { data, flattenedData } = useEmailSearchQuery({
    q: "",
    ordering: "-publish_date",
    status: ["sent", "imported"],
    types: computed((): EmailType[] | undefined => {
      switch (toValue(visibility) ?? "all") {
        case "all": {
          return undefined;
        }
        case "premium": {
          return PREMIUM_TYPES;
        }
        case "public": {
          return PUBLIC_TYPES;
        }
      }
    }),
    enabled: enabled,
    maintainData: true,
  });

  const latestEmail = computed(() => {
    if (!data.value) {
      return undefined;
    }

    return flattenedData.value.at(0) ?? null;
  });

  return latestEmail;
};
